/**
 * Conversion-scrub data layer — FS-823 (Wave 3 dash).
 *
 * Thin typed wrappers over /api/v1/conversion-scrub. Raw `fetch()` is confined
 * to this file per the dash contract (rule D6). Targets the backend's bulk
 * PATCH surface: one GET for the pending queue, one PATCH for the bulk
 * approve/reject decision (both operations share the same endpoint).
 *
 * Reason codes mirror the backend enum exactly.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ScrubStatus = "pending" | "approved" | "rejected";

export const REASON_CODES = [
	"fraud",
	"test",
	"duplicate",
	"unqualified",
	"other",
] as const;

export type ReasonCode = (typeof REASON_CODES)[number];

export function isReasonCode(v: unknown): v is ReasonCode {
	return (
		typeof v === "string" && (REASON_CODES as readonly string[]).includes(v)
	);
}

export const REASON_LABELS: Record<ReasonCode, string> = {
	fraud: "Fraud",
	test: "Test traffic",
	duplicate: "Duplicate",
	unqualified: "Unqualified",
	other: "Other",
};

export type BulkDecision = "approved" | "rejected";

export interface ConversionScrubRow {
	id: string;
	tenant_id: string;
	conversion_id: string;
	status: ScrubStatus;
	timestamp: number;
	affiliate_id: string | null;
	affiliate_name: string | null;
	campaign_id: string | null;
	campaign_name: string | null;
	offer_id: string | null;
	offer_name: string | null;
	payout_cents: number;
	ip_address: string | null;
	fraud_score: number;
	reason: ReasonCode | null;
	note: string | null;
	reviewed_by: string | null;
	reviewed_at: number | null;
	created_at: number;
	[key: string]: unknown;
}

export interface ListConversionScrubResponse {
	count: number;
	items: ConversionScrubRow[];
}

export interface BulkDecideBody {
	ids: string[];
	decision: BulkDecision;
	reason: ReasonCode;
	note?: string;
}

export interface BulkDecideResponse {
	updated: number;
	decision: BulkDecision;
	reason: ReasonCode;
}

// ---------------------------------------------------------------------------
// Remote
// ---------------------------------------------------------------------------

const BASE = "/api/v1/conversion-scrub";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export interface ListPendingArgs {
	limit?: number;
	campaign?: string;
	affiliate?: string;
	fraud_min?: number;
	from?: string;
	to?: string;
}

export async function listPendingConversions(
	args: ListPendingArgs = {},
): Promise<ListConversionScrubResponse> {
	const url = new URL(BASE, "http://local");
	if (typeof args.limit === "number") url.searchParams.set("limit", String(args.limit));
	if (args.campaign) url.searchParams.set("campaign", args.campaign);
	if (args.affiliate) url.searchParams.set("affiliate", args.affiliate);
	if (typeof args.fraud_min === "number")
		url.searchParams.set("fraud_min", String(args.fraud_min));
	if (args.from) url.searchParams.set("from", args.from);
	if (args.to) url.searchParams.set("to", args.to);
	const path = `${url.pathname}${url.search}`;
	const res = await fetch(path, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListConversionScrubResponse>(res);
}

/** PATCH /api/v1/conversion-scrub — bulk approve/reject with reason code. */
export async function bulkDecide(
	body: BulkDecideBody,
): Promise<BulkDecideResponse> {
	const res = await fetch(BASE, {
		method: "PATCH",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<BulkDecideResponse>(res);
}

// ---------------------------------------------------------------------------
// Back-compat aliases (required by scaffold contract — kept as thin wrappers)
// ---------------------------------------------------------------------------

export async function list(): Promise<ListConversionScrubResponse> {
	return listPendingConversions();
}

export async function create(
	body: BulkDecideBody,
): Promise<BulkDecideResponse> {
	return bulkDecide(body);
}

export async function remove(_id: string): Promise<{ removed: boolean }> {
	// conversion-scrub has no single-row delete; decision is bulk-only.
	throw new Error("remove: not supported — use bulkDecide");
}

// ---------------------------------------------------------------------------
// Presentation helpers (pure, unit-testable)
// ---------------------------------------------------------------------------

export function formatCurrency(cents: number | null | undefined): string {
	if (typeof cents !== "number" || !Number.isFinite(cents)) return "$0.00";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
	}).format(cents / 100);
}

export function formatDateTime(unixSec: number | null | undefined): string {
	if (
		typeof unixSec !== "number" ||
		!Number.isFinite(unixSec) ||
		unixSec <= 0
	)
		return "—";
	const d = new Date(unixSec * 1000);
	return d.toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

export function fraudBand(
	score: number | null | undefined,
): "high" | "medium" | "low" {
	const n = typeof score === "number" && Number.isFinite(score) ? score : 0;
	if (n >= 70) return "high";
	if (n >= 40) return "medium";
	return "low";
}

export function maskIp(ip: string | null | undefined): string {
	if (!ip) return "—";
	const parts = ip.split(".");
	if (parts.length === 4) return `${parts[0]}.${parts[1]}.*.*`;
	// IPv6 — show first 2 groups.
	const groups = ip.split(":");
	if (groups.length >= 2) return `${groups[0]}:${groups[1]}::*`;
	return ip.slice(0, 6) + "…";
}

export const conversionScrubQueryKeys = {
	all: ["conversion-scrub"] as const,
	list: () => [...conversionScrubQueryKeys.all] as const,
	detail: (id: string) => [...conversionScrubQueryKeys.all, id] as const,
};
